/**
 * Sprite Sheet Utility
 * 
 * Handles loading and extracting sprites from sprite sheets.
 */

export class SpriteSheet {
    constructor(imagePath, spriteWidth, spriteHeight, columns = null, rows = null) {
        this.imagePath = imagePath;
        this.spriteWidth = spriteWidth;
        this.spriteHeight = spriteHeight;
        this.columns = columns; // Number of columns in the grid
        this.rows = rows; // Number of rows in the grid
        this.image = null;
        this.loaded = false;
    }
    
    /**
     * Load the sprite sheet image
     */
    async load() {
        if (this.loaded && this.image) {
            return Promise.resolve(this.image);
        }
        
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                this.image = img;
                this.loaded = true;
                resolve(img);
            };
            img.onerror = reject;
            img.src = this.imagePath;
        });
    }
    
    /**
     * Get a sprite from the sheet by row and column
     * @param {number} row - Row index (0-based)
     * @param {number} col - Column index (0-based)
     * @returns {HTMLCanvasElement} Canvas element with the sprite
     */
    getSprite(row, col) {
        if (!this.loaded || !this.image) {
            throw new Error('Sprite sheet not loaded. Call load() first.');
        }
        
        // Calculate sprite dimensions from image size if columns/rows are provided
        let spriteWidth = this.spriteWidth;
        let spriteHeight = this.spriteHeight;
        
        if (this.columns && this.rows && this.image) {
            spriteWidth = Math.floor(this.image.naturalWidth / this.columns);
            spriteHeight = Math.floor(this.image.naturalHeight / this.rows);
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = spriteWidth;
        canvas.height = spriteHeight;
        const ctx = canvas.getContext('2d');
        
        const sx = col * spriteWidth;
        const sy = row * spriteHeight;
        
        ctx.drawImage(
            this.image,
            sx, sy, spriteWidth, spriteHeight,
            0, 0, spriteWidth, spriteHeight
        );
        
        return canvas;
    }
    
    /**
     * Get sprite as data URL
     * @param {number} row - Row index (0-based)
     * @param {number} col - Column index (0-based)
     * @returns {string} Data URL of the sprite
     */
    getSpriteDataURL(row, col) {
        const canvas = this.getSprite(row, col);
        return canvas.toDataURL();
    }
}

/**
 * Military Buildings Sprite Sheet
 * 
 * Layout: 4 columns × 6 rows (4 across, 6 down)
 * Row 0: Barracks, Training Arena, Stables, Sorcerers Tower
 * Row 1: Academy, Blacksmith, Port, Guard House
 * Row 2: Castle, Basilica, Wall, Ranger Post
 * Row 3: Sentinel Post, Level 0 Wall, Triari Post, Priest Post
 * Row 4: Ballista Post, Equine Barricade, Rune Barricade, Snag Barricade
 * Row 5: Veiled Barricade (only 1 item, other 3 spots empty)
 */
export const MILBUILDINGS_SPRITE_MAP = {
    // Row 0 (0-based indexing)
    'building_barracks': { row: 0, col: 0 },           // Barracks
    'building_training_ground': { row: 0, col: 1 },  // Training Arena
    'building_stable': { row: 0, col: 2 },            // Stables
    'building_moonglow': { row: 0, col: 3 },         // Sorcerers Tower
    
    // Row 1
    'building_temple': { row: 1, col: 0 },            // Academy
    'building_workshop': { row: 1, col: 1 },         // Blacksmith
    'building_wharf': { row: 1, col: 2 },            // Wharf
    'building_guard_house': { row: 1, col: 3 },      // Guard House
    
    // Row 2
    'building_castle': { row: 2, col: 0 },            // Castle
    'building_townhall': { row: 2, col: 1 },          // Basilica
    'building_wall': { row: 2, col: 2 },              // Wall
    'ranger_post': { row: 2, col: 3 },               // Ranger Post (not a building constant, but in sprite)
    
    // Row 3
    'sentinel_post': { row: 3, col: 0 },              // Sentinel Post
    'level_0_wall': { row: 3, col: 1 },               // Level 0 Wall
    'triari_post': { row: 3, col: 2 },                // Triari Post
    'priest_post': { row: 3, col: 3 },                // Priest Post
    
    // Row 4
    'ballista_post': { row: 4, col: 0 },              // Ballista Post
    'equine_barricade': { row: 4, col: 1 },           // Equine Barricade
    'rune_barricade': { row: 4, col: 2 },             // Rune Barricade
    'snag_barricade': { row: 4, col: 3 },             // Snag Barricade
    
    // Row 5 (only 1 item, other 3 spots empty)
    'veiled_barricade': { row: 5, col: 0 }            // Veiled Barricade
};

// Sprite sheet instance (64x64 sprites)
let milbuildingsSpriteSheet = null;

/**
 * Get the military buildings sprite sheet instance
 * Layout: 4 columns × 6 rows
 */
import { getImagePath } from './imagePath.js';

export function getMilbuildingsSpriteSheet() {
    if (!milbuildingsSpriteSheet) {
        const imagePath = getImagePath('milbuildings.png');
        milbuildingsSpriteSheet = new SpriteSheet(
            imagePath,
            64, // default sprite width (will be calculated from image dimensions)
            64, // default sprite height (will be calculated from image dimensions)
            4,  // columns
            6   // rows
        );
    }
    return milbuildingsSpriteSheet;
}

/**
 * Get sprite data URL for a building
 * @param {string} buildingKey - Building key (e.g., 'building_barracks')
 * @returns {Promise<string>} Data URL of the sprite
 */
export async function getMilbuildingSprite(buildingKey) {
    const spriteMap = MILBUILDINGS_SPRITE_MAP[buildingKey];
    if (!spriteMap) {
        return null;
    }
    
    const sheet = getMilbuildingsSpriteSheet();
    await sheet.load();
    return sheet.getSpriteDataURL(spriteMap.row, spriteMap.col);
}

/**
 * Resource Buildings Sprite Sheet
 * 
 * Layout: 4 columns × 5 rows (4 across, 5 down) - evenly divided
 * Row 0: Forester Hut, Stone Mine, Iron Mine, Farm Estate
 * Row 1: Forest Node, Stone Node, Iron Node, Lake Node
 * Row 2: Sawmill, Mason Hut, Smelter, Grain Mill
 * Row 3: Forum, Port, Villa, Temple
 * Row 4: (to be determined - user needs to provide what's in row 4)
 */
export const RESBUILDINGS_SPRITE_MAP = {
    // Row 0 - Resource Production Buildings
    'building_forester': { row: 0, col: 0 },           // Forester Hut
    'building_quarry': { row: 0, col: 1 },          // Stone Mine
    'building_mine': { row: 0, col: 2 },             // Iron Mine
    'building_farm': { row: 0, col: 3 },             // Farm Estate
    
    // Row 1 - Resource Nodes
    'resource_wood': { row: 1, col: 0 },             // Forest Node
    'resource_stone': { row: 1, col: 1 },            // Stone Node
    'resource_iron': { row: 1, col: 2 },             // Iron Node
    'resource_lake': { row: 1, col: 3 },             // Lake Node
    
    // Row 2 - Refinery Buildings
    'building_sawmill': { row: 2, col: 0 },           // Sawmill
    'building_stonemason': { row: 2, col: 1 },        // Mason Hut
    'building_foundry': { row: 2, col: 2 },          // Smelter
    'building_mill': { row: 2, col: 3 },             // Grain Mill
    
    // Row 3 - Commerce Buildings
    'building_bazaar': { row: 3, col: 0 },           // Bazaar
    'building_wharf': { row: 3, col: 1 },           // Wharf
    'building_manor': { row: 3, col: 2 },           // Manor
    'building_temple': { row: 3, col: 3 }             // Temple (Note: also in milbuildings, but this is the resource version)
    
    // Row 4 - (to be populated when user provides the layout)
    // Currently empty - waiting for user to identify what buildings are in row 4
};

// Sprite sheet instance (64x64 sprites)
let resbuildingsSpriteSheet = null;

/**
 * Get the resource buildings sprite sheet instance
 * Layout: 4 columns × 5 rows
 */
export function getResbuildingsSpriteSheet() {
    if (!resbuildingsSpriteSheet) {
        const imagePath = getImagePath('resbuildings.png');
        resbuildingsSpriteSheet = new SpriteSheet(
            imagePath,
            64, // default sprite width (will be calculated from image dimensions)
            64, // default sprite height (will be calculated from image dimensions)
            4,  // columns
            5   // rows
        );
    }
    return resbuildingsSpriteSheet;
}

/**
 * Get sprite data URL for a resource building
 * @param {string} buildingKey - Building key (e.g., 'building_forester')
 * @returns {Promise<string>} Data URL of the sprite
 */
export async function getResbuildingSprite(buildingKey) {
    const spriteMap = RESBUILDINGS_SPRITE_MAP[buildingKey];
    if (!spriteMap) {
        return null;
    }
    
    const sheet = getResbuildingsSpriteSheet();
    await sheet.load();
    return sheet.getSpriteDataURL(spriteMap.row, spriteMap.col);
}

